import type { AdvisorDraft, AdvisorDraftDay, AdvisorDraftLeg } from './types';

const nextId = (prefix: string): string =>
  `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

/** The trip's departure day: plans, but no hotel night. */
export const isDepartureDay = (day: AdvisorDraftDay): boolean =>
  day.departure === true;

/** A chapter's days that are nights (all but the departure day). */
export const nightDays = (leg: AdvisorDraftLeg): AdvisorDraftDay[] =>
  leg.days.filter((day) => !isDepartureDay(day));

/**
 * Keeps one departure day at the very end of the trip, after its last night:
 * added once the trip has a night, moved to a new last chapter, merged when
 * two were brought together, and dropped when no night is left. It never
 * holds a hotel. Returns the same draft when nothing changes.
 */
export const withDepartureDay = (draft: AdvisorDraft): AdvisorDraft => {
  const departures = draft.legs.flatMap((leg) =>
    leg.days.filter(isDepartureDay)
  );
  const nights = draft.legs.reduce(
    (total, leg) => total + nightDays(leg).length,
    0
  );
  const lastLeg = draft.legs[draft.legs.length - 1];
  const current = departures[0];
  const settled =
    departures.length === 1 &&
    current !== undefined &&
    nights > 0 &&
    lastLeg?.days[lastLeg.days.length - 1] === current &&
    !current.items.some((item) => item.kind === 'hotel');
  if (settled || (departures.length === 0 && nights === 0)) return draft;

  const legs = draft.legs.map((leg) => ({ ...leg, days: nightDays(leg) }));
  if (nights === 0) return { ...draft, legs };
  const departure: AdvisorDraftDay = {
    ...(current ?? { id: nextId('day'), items: [] }),
    departure: true,
    items: departures
      .flatMap((day) => day.items)
      .filter((item) => item.kind !== 'hotel'),
  };
  const last = legs[legs.length - 1];
  if (last) last.days = [...last.days, departure];
  return { ...draft, legs };
};
